/**
 * 顧客のサブスク情報(getSubscription)を担う
 * まず全てのサブスク情報を取得し、その後に最新の契約idを利用して、詳細情報を取得して返す
 */
import { useState, useEffect, useCallback } from 'react';
import { useFetch, FetchApiType } from '../common/useFetch';
import { useEmitMessage } from '../common/useEmitMessage';

export const useSubscription = (
  token: string,
): UseSubscription => {

  const emitMessage = useEmitMessage();
  const failed = () => emitMessage('app-error', 'keep');

  const [getAllSubscriptionsState, getAllSubscriptions] = 
    useFetch<SubscriptionOverviewItem[]>('getSubscriptions', token, null, failed);

  const [getSubscriptionDetailState, getSubscriptionDetail] =
   useFetch<Subscription>('getSubscription', token, null, failed);

  const [subscription, setSubscription] = useState<SubscriptionState>();

  // 初期化。契約一覧情報を取得する
  useEffect(() => {
    if (token !== '') getAllSubscriptions();
  }, [token]);

  // 一覧情報取得後。最新契約のIDを抽出し、そこから最新契約の詳細情報を取得する
  useEffect(() => {
    const subscriptionsData = getAllSubscriptionsState.data || [];

    if (subscriptionsData.length === 0) return;
    const contractId = subscriptionsData[0].subscriptionContractId
    getSubscriptionDetail({ subscriptionId: contractId });
  }, [getAllSubscriptionsState]);

  // 詳細情報取得後。サブスク情報を更新する
  useEffect(() => {
   if (!getSubscriptionDetailState.data) return;
   setSubscription(getSubscriptionDetailState.data);
  }, [getSubscriptionDetailState])

  // 契約情報再取得用の関数
  const getSubscription = useCallback(() => {
    getAllSubscriptions();
  }, [token]);

  return [
    { 
      subscription,
      loadingSubscriptions: getAllSubscriptionsState.loading || getSubscriptionDetailState.loading
    },
    {
      getSubscription,
      setSubscription
    }
  ];
};

// types

export type UseSubscription = [
  {
    subscription: SubscriptionState,
    loadingSubscriptions: boolean
  },
  {
    getSubscription: FetchApiType
    setSubscription: SetSubscription
  }
]
export type SubscriptionState = Subscription | undefined
export type SetSubscription = React.Dispatch<React.SetStateAction<SubscriptionState>>

// 一覧情報取得(getSubscription"s")で取得可能な契約情報
type SubscriptionOverviewItem = {
  originOrderId: string;
  originOrderToken: string;
  originOrderName: string;
  customerDisplayName: string;
  customerEmail: string; // ? 多分いないが念のため記述している
  nextBillingDate: Date;
  subscriptionContractId: string;
  billingPolicyInterval: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';
  billingPolicyIntervalCount: number;
  billingPolicyMinCycles: number;
  billingPolicyMaxCycles: number;
  status: SubscriptionStatus;
  deliveryPriceAmount: number;
  deliveryPriceCurrencyCode: string;
  deliveryAddress1: string;
  deliveryAddress2: string;
  deliveryCity: string;
  deliveryCompany: string;
  deliveryCountry: string;
  deliveryCountryCode: string;
  deliveryFirstName: string;
  deliveryLastName: string;
  deliveryName: string;
  deliveryPhone: string;
  deliveryProvince: string;
  deliveryProvinceCode: string;
  deliveryZip: string;
  createdAt: string;
  deliveryDays: number;
  deliveryTime: DeliveryTimeValue;
  subscriptionLines: SubscriptionLine[];
  customer: Customer;
  shop: Shop;
  billingAttempts: BillingAttempt[];
  customerId: string;
  rank: Rank;
}

// 詳細情報取得(getSubscription)で取得可能な契約情報、従来のデータから拡張されている
export interface Subscription extends SubscriptionOverviewItem {
  customerPaymentMethodId: string;
  deliveryTimeText: string;
  subscriptionHistories: SubscriptionHistory[];
  paymentType: string;
  shop: ShopDetail;
  subscriptionLines: SubscriptionLineDetail[];
  billingAttempts: BillingAttemptDetail[];
};

type SubscriptionLine = {
  productId: string;
  variantId: string;
  variantImage?: string;
  title: string;
  onlineStorePreviewUrl: string;
  variantTitle: string;
  sku?: string;
  quantity: number;
  currentPriceAmount: number; // 値引き後価格
  currentPriceCurrencyCode: string;
  lineDiscountedPriceAmount: number; // 値引き前価格
  lineDiscountedPriceCurrencyCode: string;
  lineId: string;
};

export interface SubscriptionLineDetail extends SubscriptionLine {
  sellingPlanId: string;
}

type SubscriptionStatus = 'ACTIVE' | 'PAUSED' | 'CANCELLED';

type Customer = {
 customerId: string;
 rank: Rank | null;
}

type SubscriptionHistory = {
  status: string;
  skipCount?: number;
  canceledAt: string;
  createdAt: string;
};

type BillingAttempt = {
  orderId?: string;
  orderToken?: string;
  orderName?: string;
  ready: boolean;
  createdAt: string;
};

interface BillingAttemptDetail extends BillingAttempt {
  errorMessage: string;
  errorCode: string | null;
}

export type DeliveryTimeValue = 'AM' | '12' | '14' | '16' | '18' | '19' | '20' | '';
export type DeliveryDaysValue = 'Mon' | 'Tue' | 'Wed' | 'Thr' | 'Fri' | 'Sat' | 'Sun';

type Shop = {
  showPause: boolean;
  showCancel: boolean;
  showSkip: boolean;
  showChangeAddress: boolean;
  mypageRemarks: string;
  deliveryDays: number;
  enableDeliveryTimesAM: boolean;
  enableDeliveryTimes12: boolean;
  enableDeliveryTimes14: boolean;
  enableDeliveryTimes16: boolean;
  enableDeliveryTimes18: boolean;
  enableDeliveryTimes19: boolean;
  enableDeliveryTimes20: boolean;
  disableDeliveryDaysMon: boolean;
  disableDeliveryDaysTue: boolean;
  disableDeliveryDaysWed: boolean;
  disableDeliveryDaysThr: boolean;
  disableDeliveryDaysFri: boolean;
  disableDeliveryDaysSat: boolean;
  disableDeliveryDaysSun: boolean;
  rankGroup: RankGroup | null;
};

interface ShopDetail extends Shop {
  showDelivery: boolean;
  mypageChangeDeliveryDays: boolean;
  showChangeProduct: boolean;
  showNewAddProduct: boolean;
  showChangePayment: boolean;
  showRemoveProduct: boolean;
  showNewOrder: boolean;
  showChangeQuantity: boolean;
  maxSkipCount: number;
  hideLowestBuySkip: boolean;
  hideLowestBuyPause: boolean;
  hideLowestBuyCancel: boolean;
  showPlanModify: boolean;
  isAllowModifyDays: boolean;
  allowModifyDays: number;
  hideLowestModify: boolean;
  showStockAlert: boolean;
  deliveryDaysMax: number;
  changeNextBillingDateWithDeliveryDate: boolean;
  enableDeliveryDateNoDay: boolean;
  deliveryTimes: DeliveryTime[];
}

type DeliveryTime = {
 tag: string;
 title: string;
}

type RankGroup = {
  public: boolean;
};

type Rank = {
  name: string;
  discountPercentage: number;
};